import apiAnnouncements from '@/domains/admin/api/announcement.api';
import { DEFAULT_PAGE_SIZE } from '@/domains/common/pagination.config';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type AdminAnnouncementInterface from '@/domains/admin/typescript/AdminAnnouncementInterface';
import type AdminAnnouncementPageDisplayEnum from '@/domains/admin/typescript/AdminAnnouncementPageDisplayEnum';

export default defineStore('adminAnnoucement', () => {
    const announcements = ref<AdminAnnouncementInterface[]>([]);
    const selectedAnnouncement = ref<AdminAnnouncementInterface>();

    const totalAnnouncements = ref(0);
    const hasAnnouncementSelected = computed<boolean>(() => selectedAnnouncement.value !== undefined);

    function selectAnnouncementById(announcementId: AdminAnnouncementInterface['id']): void {
        selectedAnnouncement.value = announcements.value?.find((announcement) => announcement.id === announcementId);
    }

    function isAnnouncementSelected(announcement: AdminAnnouncementInterface): boolean {
        return selectedAnnouncement.value?.id === announcement.id;
    }

    function unselectAnnouncement(): void {
        selectedAnnouncement.value = undefined;
    }

    async function fetchAnnouncements(page = 1, limit = DEFAULT_PAGE_SIZE) {
        const { entities, total } = await apiAnnouncements.getAnnouncements({ page, limit });
        announcements.value = entities;
        totalAnnouncements.value = total;
    }

    async function fetchActivatedAnnouncements() {
        announcements.value = await apiAnnouncements.getActivatedAnnouncements();
    }

    function getAnnouncementById(id: number): AdminAnnouncementInterface | undefined {
        return announcements.value?.find((announcement) => announcement.id === id);
    }

    const getActiveAnnouncements = computed<AdminAnnouncementInterface[] | undefined>(() => {
        return announcements.value?.filter((announcement) => announcement.isActivated);
    });

    function getActiveAnnouncementsByLocation(
        location: AdminAnnouncementPageDisplayEnum,
    ): AdminAnnouncementInterface[] {
        return (
            announcements.value?.filter(
                (announcement) => announcement.isActivated && announcement.pageToDisplay === location,
            ) ?? []
        );
    }

    function removeAnnouncement(id: number): void {
        const index = announcements.value?.findIndex((announcement) => announcement.id === id);
        if (index !== undefined && index !== -1) {
            announcements.value?.splice(index, 1);
            totalAnnouncements.value--;
        }
    }

    function $reset() {
        announcements.value = [];
        totalAnnouncements.value = 0;
        selectedAnnouncement.value = undefined;
    }

    return {
        announcements,
        selectedAnnouncement,
        hasAnnouncementSelected,
        totalAnnouncements,
        selectAnnouncementById,
        isAnnouncementSelected,
        unselectAnnouncement,
        getAnnouncementById,
        getActiveAnnouncements,
        getActiveAnnouncementsByLocation,
        removeAnnouncement,

        fetchAnnouncements,
        fetchActivatedAnnouncements,

        $reset,
    };
});
